import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SUBMENU_PARAM_ACTIVE_VALUE } from '../../../constants/route'
import {
  useModals,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import Button from '../../Button'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import {
  getProviderTableMassActions,
  PROVIDER_TABLE_MODALS_CONFIG,
  ProviderTableLayout,
  useProviderTableActions,
  useProviderTablePage,
} from '../../Provider'
import { TablePagination } from '../../TablePagination'
import {
  getEntityTabFilterStateApi,
  updateEntityTabTableConfig,
} from '../EntityTab.api'

import { QA_NAME_ENTITY_TAB_PROVIDERS_ADD_NEW } from './EntityTabProviders.constants'

import * as styles from './EntityTabProviders.module.css'

import PlusIcon from '../../../icons/plus.svg'
import ProvidersIcon from '../../../icons/providers.svg'

function EntityTabProviders({
  entity,
  qaName = QA_NAME_ENTITY_TAB_PROVIDERS_ADD_NEW,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  onCreate,
  onEntityReload,
  onRegisterReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(PROVIDER_TABLE_MODALS_CONFIG)

  const scopeFilters = scopeFilterKey ? { [scopeFilterKey]: [scopeId] } : null

  async function handleApplyTableConfig(data) {
    return updateEntityTabTableConfig(data, entity.plural)
  }

  const tablePage = useProviderTablePage({
    asyncTableOptions: {
      extraFilters: { ...scopeFilters, sub_menu: SUBMENU_PARAM_ACTIVE_VALUE },
      filterStateApi: getEntityTabFilterStateApi({
        entityPlural: entity.plural,
        objectId: scopeId,
        sub: SUBMENU_PARAM_ACTIVE_VALUE,
      }),
      withoutSubMenu: true,
    },
    entity,
    extraSelectionResetDeps: [scopeId],
    isBrowserTabTitleEnabled: false,
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    onApplyTableConfig: handleApplyTableConfig,
  })

  const {
    excludeIds,
    filterValues,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleSetPagination,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selectedIds,
  } = tablePage

  async function handleReloadAfterMassAction() {
    onEntityReload?.()

    return handleReload()
  }

  const {
    getProviderItemActions,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  } = useProviderTableActions({
    excludeIds,
    filterValues: { ...filterValues, ...scopeFilters },
    pagination,
    selectAllPages,
    selectedIds,
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    onCleanSelection: handleCleanSelection,
    onCloseEmailFormModal: closeEmailFormModal,
    onCloseModal: closeModal,
    onReload: handleReloadAfterMassAction,
    onSetEmailFormModal: setEmailFormModal,
    onSetModal: setModal,
  })

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
  })

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  function handleAddProvider() {
    onCreate?.()
  }

  const massActions = getProviderTableMassActions(permissions, t)

  const hasProviders = items?.items?.length > 0

  const hasTable = hasProviders || isLoading

  const shouldCollapseToolbar = !hasProviders && !hasFilterChanged && !isLoading

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName,
    isLoading,
    qaName,
    shouldShowActions: shouldShowCreateAction && !hasFilterChanged,
    text: t('ProviderTable.AddNewBtnKey'),
    onClick: handleAddProvider,
  })

  return (
    <div className={styles.EntityTabProviders} ref={pageScrollWrapperRef}>
      {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
      {shouldCollapseToolbar || !shouldShowCreateAction ? null : (
        <div className={styles.toolbar}>
          <PageActions className={styles.actions} isLoading={isLoading}>
            <Button.Text
              beforeIconProps={{ Icon: PlusIcon }}
              isSkeleton={isLoading}
              qaName={qaName}
              text={t('ProviderTable.AddNewBtnKey')}
              onClick={handleAddProvider}
            />
          </PageActions>
        </div>
      )}
      <ProviderTableLayout
        emptyState={
          <PageEmptyState
            EntityIcon={ProvidersIcon}
            actions={emptyStateActions}
            className={styles.emptyPage}
            text={t('ProviderTable.NoDataKey')}
          />
        }
        extraModals={modals.map(([id, element]) => (
          <Fragment key={id}>{element}</Fragment>
        ))}
        getProviderItemActions={getProviderItemActions}
        hasTable={hasTable}
        massActions={massActions}
        setItemAction={setItemAction}
        setMassAction={setMassAction}
        tablePage={tablePage}
      />
      {hasTable ? (
        <TablePagination
          responsive
          className={styles.pagination}
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      ) : null}
    </div>
  )
}

// This module is reachable from the Email barrel (via RequestProfile tabs), so it
// must not read Email exports at module scope — consumers apply withDraftProviders
// with EmailDraftTableProvider themselves to avoid a circular import crash.
export default EntityTabProviders
